import { Store } from '@campaign/domain/store';
import {
  resetLoggedOutUserSession,
  setLoggedInUserWithoutToken,
  setRefreshAccessToken,
  updateLoading,
} from '@campaign/domain/store/reducers';
import * as network from '@campaign/side-effects/network';
import { NetworkSessionUserDetails } from '@campaign/types/network';
import { buildLoginRedirectUrl } from '@campaign/utils/idpfRedirect';
import { createLogger } from '@campaign/utils/logger';
import { syncCampaignTabFromUrl } from '../campaignTab';
import { loadPublicCampaigns } from '../loadPublicData';
import { loadUserData } from '../user';

const logger = createLogger('@domain/middlewares/auth/utils');

/**
 * Refresh access token
 *
 * @remarks
 * - Check if the access token in the state is valid
 * - If invalid, refresh the token.
 * - If the request for the refreshing token is failed,
 * - redirect to login page or login page on IDPF if already on login page
 */
export async function refreshAccessToken(): Promise<string | null> {
  logger.debug('refreshAccessToken');
  try {
    const { accessToken } = await network.requestNewAccessToken();

    return accessToken;
  } catch {
    return null;
  }
}

/**
 * Get session of user
 *
 * @returns Promise<NetworkSessionUserDetails>
 */
export async function getSessionUser(): Promise<NetworkSessionUserDetails> {
  const { user } = await network.getSessionUser();
  if (!user) {
    throw new Error(`Invalid session user: ${user}`);
  }
  logger.info('user', user);

  return user;
}

/**
 * Redirect to login page
 *
 * @remarks
 * If the user is already on login page, redirect to IDPF login page
 */
export function redirectToLoginPage() {
  logger.debug('current path:', location.pathname);
  logger.debug('Redirect to IDPF login page');
  location.href = buildLoginRedirectUrl();
}

/**
 * Initialize user session
 *
 * @param store Store
 * @param fromLogin boolean - throw error and redirect to login page if refresh token request fail
 */
export async function initializeSession(
  store: Store,
  fromLogin = false
): Promise<void> {
  const token = await refreshAccessToken();

  if (!token) {
    logger.debug('initializeSession: Session error');
    resetLoggedOutUserSession(store);
    void loadPublicCampaigns(store, false);
    syncCampaignTabFromUrl(store);
    if (fromLogin) {
      redirectToLoginPage();
      throw new Error('Login failed');
    }
    return;
  }

  logger.info(token);
  setRefreshAccessToken(store, token);

  try {
    const user = await getSessionUser();

    setLoggedInUserWithoutToken(store, {
      firstName: user.firstName,
      hasPhoneNumber: Boolean(user.phoneNumber),
      id: user.userId,
      isLogged: true,
      lastName: user.lastName,
    });

    syncCampaignTabFromUrl(store);
    void loadPublicCampaigns(store, true);
    updateLoading(store, 'userCampaignList', true);
    void loadUserData(store);
  } catch (error) {
    logger.error('[initializeSession]', error);
    resetLoggedOutUserSession(store);
    void loadPublicCampaigns(store, false);
    syncCampaignTabFromUrl(store);

    if (fromLogin) {
      redirectToLoginPage();
      throw new Error('Login failed');
    }
  }
}

/**
 * Initialize app authentication
 *
 * @param store Store
 */
export async function initializeAppAuthentication(store: Store): Promise<void> {
  logger.debug('initializeAppAuthentication');
  try {
    await initializeSession(store, false);
  } catch (error) {
    logger.error('[initializeAppAuthentication]', error);
  }
}
